import {
    AuthorizationInfo,
    ClientTransferringSubmitInput, HourField, ReportServiceType,
    serviceTypeMapping,
    serviceTypeOptionsAvailableForAllTiers
} from "../../domain";
import {Subject} from "rxjs";
import {FormProvider, useForm} from "react-hook-form";
import _ from "lodash";
import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import React, {useCallback} from "react";
import {showToast} from "common";
import {ReportModal} from "../../form-components/ReportModal";
import {useObservable} from "rxsupport";
import {ControlledOfferedHourInput} from "../../form-components/auth-hours/ClientOfferInput";
import {FatOutlineButton} from "common/component/FatOutlineButton";
import {ServiceTypeSingleSelectInput} from "../../form-components/auth-hours/ServiceTypesSingleSelectInput";
import {ServiceTypesMultiselectInput} from "../../form-components/auth-hours/ServiceTypesMultiselectInput";
import {LoadingSpinner} from "../../../../common";
import {ModalDatePicker} from "../../form-components/ModalDatePicker";

type ClientTransferringFormModalProps = {
    authorization: AuthorizationInfo;
    visible$: Subject<boolean>;
    onFinished: (formValues: ClientTransferringSubmitInput | null) => Promise<void>;
    isAuth?: boolean;
}

const serviceTypeInverse = Object.fromEntries(Object.entries(serviceTypeMapping).map((mapping) => mapping.reverse()));
const mapping: Map<ReportServiceType, HourField[]> = new Map([
   [
        ReportServiceType.Compass_Behavioral,
        [
            'offeredHighLevelHours'
        ],
    ],
]);

const customizedServiceTypeMappedToHourFieldsForClientTransferring = Object.freeze(mapping);
export function ClientTransferringFormModal({
                                                authorization,
                                                visible$,
                                                onFinished
                                            }: ClientTransferringFormModalProps) {
    const restrictedOptions = serviceTypeOptionsAvailableForAllTiers(authorization)?.options ?? null;
    const visible = useObservable(visible$, false);
    const defaultServiceType = authorization.serviceType1.split(';')
        .map((str) => str.trim())
        .filter((str) => !_.isEmpty(str))
        .sort()
        .map(st => serviceTypeInverse[st]);

    const emptyHours = {
        offeredParaprofessionalHours: null,
        offeredMidLevelHours: null,
        offeredHighLevelHours: null,
        offeredGroupCareHours: null,
    }

    const methods = useForm<ClientTransferringSubmitInput>({
        defaultValues: {
            ...emptyHours,
            serviceType: defaultServiceType,
        }
    });

    const {promiseInProgress} = usePromiseTracker();

    const onCancel = useCallback(async () => {
        showToast.warning('Submission for Client Transferring is cancelled');
        methods.reset();
        visible$.next(false);
    }, [methods, visible$]);

    const submitWithTracking = useCallback((formValues: ClientTransferringSubmitInput | null) => trackPromise(onFinished(formValues)), [onFinished]);

    const submit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await methods.handleSubmit(async (data: ClientTransferringSubmitInput) => {
            await submitWithTracking(_.pick(data, [
                'offeredStartDate',
                'offeredParaprofessionalHours',
                'offeredMidLevelHours',
                'offeredHighLevelHours',
                'offeredGroupCareHours',
                'serviceType',
            ]));
            methods.reset();
            visible$.next(false);
        })(e);
    }, [methods, visible$, submitWithTracking]);

    return (
        <ReportModal visible={visible} title={'Submit Client Transferring'} onClose={onCancel}>
            <FormProvider {...methods}>
                <form autoComplete="off" onSubmit={submit}>
                    {!promiseInProgress && (<>
                        <div className="space-y-6">
                            <div className="flex flex-col w-full p-4 scrollbar-always"
                                 style={{maxHeight: "calc(100vh - 68px - 100px - 37px - 90px - 60px)"}}>
                                <div className="font-bold italic pb-2">
                                    Offered Services
                                </div>
                                {restrictedOptions !== null &&
                                    <ServiceTypeSingleSelectInput restrictedOptions={restrictedOptions}
                                                                  callback={(newSt) => methods.reset({
                                                                      serviceType: newSt,
                                                                      ...emptyHours
                                                                  })}
                                                                  disabled={promiseInProgress}/>
                                }
                                {restrictedOptions === null &&
                                    <ServiceTypesMultiselectInput disabled={promiseInProgress}/>
                                }

                                <div className="font-bold italic pb-2">
                                    Offered Hours during Transfer
                                </div>
                                <div className={"flex w-full pb-4"}>
                                    <div className="justify-center w-1/2">
                                        <ModalDatePicker<ClientTransferringSubmitInput>
                                            disabled={promiseInProgress}
                                            label={'Offer Start Date'}
                                            field={'offeredStartDate'}
                                        />
                                    </div>
                                </div>
                                <div className={"w-full flex flex-wrap"}>
                                    <ControlledOfferedHourInput field={"offeredParaprofessionalHours"}
                                                                label="Paraprofessional Hours"
                                                                disabled={promiseInProgress}
                                                                additionalServiceTypeMapping={customizedServiceTypeMappedToHourFieldsForClientTransferring}
                                                                isCurrentOffer
                                                                required/>
                                    <ControlledOfferedHourInput field={"offeredMidLevelHours"} label="Mid-level Hours"
                                                                disabled={promiseInProgress}
                                                                isCurrentOffer
                                                                additionalServiceTypeMapping={customizedServiceTypeMappedToHourFieldsForClientTransferring}
                                                                required/>
                                    <ControlledOfferedHourInput field={"offeredHighLevelHours"} label="High-level Hours"
                                                                disabled={promiseInProgress}
                                                                isCurrentOffer
                                                                additionalServiceTypeMapping={customizedServiceTypeMappedToHourFieldsForClientTransferring}
                                                                required/>
                                    <ControlledOfferedHourInput field={"offeredGroupCareHours"} label="Group Care Hours"
                                                                disabled={promiseInProgress}
                                                                isCurrentOffer
                                                                additionalServiceTypeMapping={customizedServiceTypeMappedToHourFieldsForClientTransferring}
                                                                required/>
                                </div>
                            </div>
                        </div>

                        <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                            <div className="flex w-1/4">
                                <FatOutlineButton color={'red'}
                                                  text={'Cancel'}
                                                  onClick={onCancel}/>
                                <span style={{width: 10}}/>
                                <FatOutlineButton type="submit" text={'Submit'} color={"green"}/>
                            </div>
                        </div>
                    </>)}
                    {promiseInProgress &&
                        <div className="p-12">
                            <LoadingSpinner text="Processing"/>
                        </div>
                    }
                </form>
            </FormProvider>
        </ReportModal>
    );
}